import type { FC, ReactNode } from 'react'
import type { ExperimentSummary, LabStatus } from '@/types'

import MainContainer from '@/components/generals/main-container'
import EditorialGrid from '@/components/generals/editorial-grid'
import Button from '@/components/elements/button'
import SchematicFigure from '@/components/content/schematic-figure'
import ExperimentItem from '@/components/lab/experiment-item'
import ExperimentStatus from '@/components/lab/experiment-status'
import { LAB_STATUSES } from '@/constants/taxonomy'

interface LabPreviewProps {
    experiments: Array<ExperimentSummary>
}

/** Dark, documentation-style band: intro and status legend, a live plate, then the log. */
const LabPreview: FC<LabPreviewProps> = ({ experiments }): ReactNode => {
    const featured = experiments.find((experiment) => experiment.status === 'active' && experiment.schematic) ?? null

    return (
        <section id='lab' aria-labelledby='lab-title' className='tone-ink'>
            <MainContainer className='py-20 md:py-32'>
                <EditorialGrid className='gap-y-14'>
                    <div className='col-span-4 flex flex-col gap-8 md:col-span-12 lg:sticky lg:top-28 lg:col-span-4 lg:self-start'>
                        <p className='flex items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                            <span aria-hidden='true' className='size-1.5 bg-brand' />
                            <span className='text-foreground'>02</span>
                            AI Lab
                        </p>

                        <h2 id='lab-title' className='font-heading text-title [font-weight:420]'>
                            A public laboratory for <em className='text-brand'>agents</em> and automation.
                        </h2>

                        <p className='max-w-[42ch] text-body text-muted-foreground'>
                            Experiments with AI agents, agent infrastructure, workflow automation and self-hosted AI,
                            written up while they run: objectives, architecture, results and the limits found along the way.
                        </p>

                        <dl className='flex flex-col border-t border-border'>
                            {(Object.keys(LAB_STATUSES) as Array<LabStatus>).map((status) => (
                                <div key={status} className='flex flex-col gap-1.5 border-b border-border py-3'>
                                    <dt><ExperimentStatus status={status} /></dt>
                                    <dd className='text-[0.875rem] leading-relaxed text-muted-foreground'>{LAB_STATUSES[status].description}</dd>
                                </div>
                            ))}
                        </dl>

                        <Button href='/lab' variant='secondary' icon='arrow' className='self-start'>
                            Visit the lab
                        </Button>
                    </div>

                    <div className='col-span-4 flex flex-col gap-14 md:col-span-12 lg:col-span-8'>
                        {featured?.schematic && (
                            <div className='flex flex-col gap-4'>
                                <p className='font-mono text-label uppercase text-muted-foreground'>
                                    Now running / {featured.code}
                                </p>
                                <SchematicFigure
                                    schematic={featured.schematic}
                                    id={`home-lab-${featured.slug}`}
                                    figure={featured.code.replace('EXP-', '')}
                                    title={featured.title}
                                />
                            </div>
                        )}

                        <div>
                            <div className='flex items-center justify-between gap-4 border-b border-foreground pb-4'>
                                <h3 className='font-mono text-label uppercase text-muted-foreground'>Lab log</h3>
                                <p className='font-mono text-caption text-muted-foreground'>{experiments.length} entries</p>
                            </div>

                            <ul aria-label='Recent experiments'>
                                {experiments.map((experiment) => (
                                    <ExperimentItem key={experiment.slug} experiment={experiment} compact />
                                ))}
                            </ul>
                        </div>
                    </div>
                </EditorialGrid>
            </MainContainer>
        </section>
    )
}

export default LabPreview
